import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface NotificationSettings {
  debateStart: boolean;
  matchFound: boolean;
  debateResult: boolean;
  systemNotice: boolean;
  sound: boolean;
  vibration: boolean;
}

export default function NotificationSettingsPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<NotificationSettings>({
    debateStart: true,
    matchFound: true,
    debateResult: true,
    systemNotice: true,
    sound: true,
    vibration: true,
  });

  const toggleSetting = (key: keyof NotificationSettings) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const ToggleSwitch = ({ 
    enabled, 
    onToggle 
  }: { 
    enabled: boolean; 
    onToggle: () => void;
  }) => (
    <button
      onClick={onToggle}
      className={`relative w-12 h-6 rounded-full transition-colors ${
        enabled ? 'bg-primary-500' : 'bg-slate-600'
      }`}
    >
      <span
        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
          enabled ? 'translate-x-7' : 'translate-x-1'
        }`}
      />
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="flex items-center p-4 border-b border-slate-700">
        <button
          onClick={() => navigate(-1)}
          className="text-slate-400 hover:text-white mr-4"
        >
          ← 뒤로
        </button>
        <h1 className="text-lg font-semibold">알림 설정</h1>
      </header>

      <div className="p-4 max-w-lg mx-auto">
        {/* 토론 알림 */}
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
            토론 알림
          </h2>
          <div className="card space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">토론 시작 알림</p>
                <p className="text-sm text-slate-400">매칭된 토론이 시작될 때 알림</p>
              </div>
              <ToggleSwitch
                enabled={settings.debateStart}
                onToggle={() => toggleSetting('debateStart')}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">매칭 완료 알림</p>
                <p className="text-sm text-slate-400">토론 상대가 매칭되었을 때 알림</p>
              </div>
              <ToggleSwitch
                enabled={settings.matchFound}
                onToggle={() => toggleSetting('matchFound')}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">토론 결과 알림</p>
                <p className="text-sm text-slate-400">토론 종료 후 결과 알림</p>
              </div>
              <ToggleSwitch
                enabled={settings.debateResult}
                onToggle={() => toggleSetting('debateResult')}
              />
            </div>
          </div>
        </div>

        {/* 시스템 알림 */}
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
            시스템 알림
          </h2>
          <div className="card space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">공지사항</p>
                <p className="text-sm text-slate-400">서비스 공지 및 업데이트 알림</p>
              </div>
              <ToggleSwitch
                enabled={settings.systemNotice}
                onToggle={() => toggleSetting('systemNotice')}
              />
            </div>
          </div>
        </div>

        {/* 알림 방식 */}
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
            알림 방식
          </h2>
          <div className="card space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">소리</p>
                <p className="text-sm text-slate-400">알림 소리 켜기/끄기</p>
              </div>
              <ToggleSwitch
                enabled={settings.sound}
                onToggle={() => toggleSetting('sound')}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">진동</p>
                <p className="text-sm text-slate-400">알림 진동 켜기/끄기</p>
              </div>
              <ToggleSwitch
                enabled={settings.vibration}
                onToggle={() => toggleSetting('vibration')}
              />
            </div>
          </div>
        </div>

        {/* 안내 */}
        <p className="text-center text-slate-500 text-sm">
          알림 설정은 현재 기기에만 적용됩니다
        </p>
      </div>
    </div>
  );
}
